import React, { useState, useEffect } from 'react';
import { FiX, FiChevronLeft, FiChevronRight, FiCheck, FiDollarSign, FiCalendar, FiFileText } from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { formatYYYYMMDDLocal } from '../../../utils/dateOnly';

interface PaymentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName?: string;
  onSuccess?: () => void;
}

interface Invoice {
  id: string;
  number: string;
  issue_date: string;
  due_date: string;
  balance_due: number;
  status: string;
  sub_total?: number;
  tax_total?: number;
}

interface PaymentForm {
  amount: number;
  date: string;
  method: 'cash' | 'check' | 'card' | 'zelle';
  reference: string;
  memo: string;
  selectedInvoices: string[];
  useStripe: boolean;
  autoApplyCredit: boolean;
}

const PaymentWizard: React.FC<PaymentWizardProps> = ({
  isOpen,
  onClose,
  customerId,
  customerName = 'Customer',
  onSuccess
}) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const { settings: businessSettings } = useBusinessSettings();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customerCredit, setCustomerCredit] = useState(0);
  const [allocationPreview, setAllocationPreview] = useState<any>(null);
  const [waiveTax, setWaiveTax] = useState<boolean>(false);

  const [form, setForm] = useState<PaymentForm>({
    amount: 0,
    date: formatYYYYMMDDLocal(businessSettings?.timezone), // Use local date format
    method: 'cash',
    reference: '',
    memo: '',
    selectedInvoices: [],
    useStripe: false,
    autoApplyCredit: false
  });
  // Separate string input to preserve user typing and enforce 2 decimals
  const [amountInput, setAmountInput] = useState<string>('');

  // Fetch customer's unpaid invoices and credit
  useEffect(() => {
    if (isOpen && customerId) {
      fetchCustomerData();
    }
  }, [isOpen, customerId]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      setError('');

      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      // Fetch unpaid invoices
      const invoicesRes = await axios.get(`${API_BASE_URL}/invoices?customer_id=${customerId}&status=open,overdue,partial_open,partial_over`, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });

      const rawInvs = invoicesRes.data.invoices || [];

      // Fetch customer to read starting_balance
      const custRes = await axios.get(`${API_BASE_URL}/customers/${customerId}`, {
        headers,
        withCredentials: isCookieMode,
      });
      const sb = Number(custRes.data?.starting_balance || 0);
      const enhanced = [...rawInvs];
      
      // If starting balance exists, show synthetic bucket to convert
      if (sb > 0) {
        enhanced.unshift({
          id: 'starting-balance',
          number: 'STARTING ACCOUNT BALANCE',
          issue_date: '1970-01-01',
          due_date: '1970-01-01',
          status: 'overdue',
          balance_due: sb,
          sub_total: sb,
          tax_total: 0,
        } as any);
      } else {
        // No raw starting_balance: attempt to include SBAL invoice if it exists
        try {
          const sbalNumber = `SBAL-${customerId}`;
          const sbalRes = await axios.get(`${API_BASE_URL}/invoices/by-number/${encodeURIComponent(sbalNumber)}`, {
            headers,
            withCredentials: isCookieMode,
          });
          const sbal = sbalRes.data;
          if (sbal && !enhanced.find((inv: any) => String(inv.id) === String(sbal.id))) {
            enhanced.unshift({
              id: sbal.id,
              number: sbal.number,
              issue_date: sbal.issue_date,
              due_date: sbal.due_date,
              status: sbal.status,
              balance_due: sbal.balance_due,
              sub_total: sbal.sub_total,
              tax_total: sbal.tax_total,
            } as any);
          }
        } catch (e) {
          // 404 or other: ignore, SBAL doesn't exist
        }
      }
      setInvoices(enhanced);

      // Fetch customer credit
      const creditRes = await axios.get(`${API_BASE_URL}/payments/customer-credits/${customerId}`, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });

      setCustomerCredit(creditRes.data.balance || 0);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PaymentForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleInvoiceToggle = (invoiceId: string) => {
    setForm(prev => ({
      ...prev,
      selectedInvoices: prev.selectedInvoices.includes(invoiceId)
        ? prev.selectedInvoices.filter(id => id !== invoiceId)
        : [...prev.selectedInvoices, invoiceId]
    }));
  };

  const calculateTotalSelected = () => {
    return invoices
      .filter(inv => form.selectedInvoices.includes(inv.id))
      .reduce((sum, inv) => sum + inv.balance_due, 0);
  };

  const canProceedToStep2 = () => {
    return form.amount > 0 && form.date && form.method;
  };

  const canProceedToStep3 = () => {
    return form.amount > 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && canProceedToStep2()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && canProceedToStep3()) {
      // If waiveTax is enabled, apply it to eligible selected invoices before preview
      if (waiveTax && form.selectedInvoices.length > 0) {
        void applyWaiveTaxThenProceed();
        return;
      }
      generateAllocationPreview();
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateAllocationPreview = () => {
    const selectedTotal = calculateTotalSelected();
    const remainingAmount = form.amount - selectedTotal;
    const unappliedInvoices = invoices.filter(inv => !form.selectedInvoices.includes(inv.id));
    let remainingForAutoApply = Math.max(0, remainingAmount);
    const appliedToOtherInvoices = form.autoApplyCredit
      ? unappliedInvoices.map(inv => {
          const amount = Math.min(inv.balance_due, remainingForAutoApply);
          remainingForAutoApply = Math.max(0, remainingForAutoApply - amount);
          return { ...inv, previewAppliedAmount: amount };
        }).filter(inv => inv.previewAppliedAmount > 0)
      : [];
    
    // Simulate allocation preview
    const preview = {
      selectedInvoices: invoices.filter(inv => form.selectedInvoices.includes(inv.id)),
      selectedTotal,
      remainingAmount,
      creditAmount: form.autoApplyCredit ? remainingForAutoApply : Math.max(0, remainingAmount),
      appliedToOtherInvoices
    };
    
    setAllocationPreview(preview);
  };

  const applyWaiveTaxThenProceed = async () => {
    try {
      const headers: any = {};
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const selected = invoices.filter(inv => form.selectedInvoices.includes(inv.id));
      const sbalOrAccount = selected.filter(inv => inv.id === 'starting-balance' || String(inv.number || '').startsWith('SBAL-'));
      const eligible = selected.filter(inv => inv.id !== 'starting-balance' && !String(inv.number || '').startsWith('SBAL-'));

      if (sbalOrAccount.length && eligible.length) {
        setError('Waive Tax is ignored for Account Balance and SBAL invoices. All other selected invoices will have tax removed.');
      } else {
        setError('');
      }

      if (eligible.length > 0) {
        await axios.post(`${API_BASE_URL}/invoices/waive-tax`, {
          invoice_ids: eligible.map(e => e.id)
        }, {
          headers,
          withCredentials: isCookieMode
        });

        // Refresh invoices to get updated sub_total/tax_total/balance_due
        await fetchCustomerData();
      }

      generateAllocationPreview();
      setCurrentStep(3);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to waive tax for selected invoices');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      if (form.useStripe && form.method === 'card') {
        // Create Stripe checkout session
        const sessionRes = await axios.post(`${API_BASE_URL}/stripe/create-checkout-session`, {
          customer_id: customerId,
          amount: form.amount,
          invoice_ids: form.selectedInvoices,
          memo: form.memo
        }, {
          headers,
          withCredentials: isCookieMode, // This sends cookies with the request
        });

        // Redirect to Stripe checkout
        window.location.href = sessionRes.data.url;
        return;
      }

      // Create regular payment
      const payRes = await axios.post(`${API_BASE_URL}/payments`, {
        customer_id: customerId,
        amount: form.amount,
        date: form.date,
        method: form.method,
        reference: form.reference,
        memo: form.memo,
        selected_invoices: form.selectedInvoices,
        auto_apply_credit: form.autoApplyCredit
      }, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });

      // Optimistic UI update: if backend returns updated finance snapshot, merge into local state
      try {
        const { customer: updatedCustomer, credit, sbalInvoice, invoices: updatedInvoices } = payRes.data || {};
        if (typeof credit === 'number') setCustomerCredit(credit);
        if (Array.isArray(updatedInvoices) && updatedInvoices.length) {
          setInvoices(prev => {
            const byId = new Map(prev.map(i => [String(i.id), i]));
            for (const u of updatedInvoices) byId.set(String(u.id), { ...(byId.get(String(u.id)) || u), ...u });
            const next = Array.from(byId.values());
            // Include SBAL if provided and not present
            if (sbalInvoice && !next.find(i => String(i.id) === String(sbalInvoice.id))) next.unshift(sbalInvoice);
            return next;
          });
        } else if (sbalInvoice) {
          setInvoices(prev => (prev.find(i => String(i.id) === String(sbalInvoice.id)) ? prev : [sbalInvoice, ...prev]));
        }
      } catch {}

      // Background refresh to ensure all balances and SBAL conversion are consistent
      await fetchCustomerData();

      onSuccess?.();
      onClose();
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
        (err.code === 'NETWORK_ERROR' ? 'Network error - please check your connection and try again' :
         err.code === 'ECONNABORTED' ? 'Request timed out - please try again' :
         'Failed to create payment - please try again');
      
      setError(errorMessage);
      
      // For Stripe errors, provide specific guidance
      if (err.response?.data?.error?.includes('Stripe')) {
        setError('Payment processing error - please check your Stripe configuration or try a different payment method');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      amount: 0,
      date: formatYYYYMMDDLocal(businessSettings?.timezone),
      method: 'cash',
      reference: '',
      memo: '',
      selectedInvoices: [],
      useStripe: false,
      autoApplyCredit: false
    });
    setAmountInput('');
    setCurrentStep(1);
    setError('');
    setAllocationPreview(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-50">
              Create Payment
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {customerName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400'
                }`}>
                  {currentStep > step ? <FiCheck className="h-4 w-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step ? 'bg-blue-600' : 'bg-neutral-200 dark:bg-neutral-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-neutral-600 dark:text-neutral-400">
            <span>Payment Details</span>
            <span>Allocate</span>
            <span>Review</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Payment Amount *
                </label>
                <div className="relative">
                  <FiDollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow empty input
                      if (raw === '') {
                        setAmountInput('');
                        setForm(prev => ({ ...prev, amount: 0 }));
                        return;
                      }
                      // Normalize and enforce 2 decimal places: keep digits and at most one dot
                      const parts = raw.replace(/[^0-9.]/g, '').split('.');
                      const intPart = parts[0].replace(/^0+(\d)/, '$1');
                      const decPart = parts.length > 1 ? parts[1].replace(/[^0-9]/g, '').slice(0, 2) : '';
                      const next = decPart !== '' ? `${intPart || '0'}.${decPart}` : (parts.length > 1 ? `${intPart || '0'}.` : intPart);
                      // Only update if it matches pattern up to 2 decimals
                      const valid = /^\d*(?:\.\d{0,2})?$/.test(next);
                      if (!valid) return;
                      setAmountInput(next);
                      const parsed = parseFloat(next);
                      setForm(prev => ({ ...prev, amount: isNaN(parsed) ? 0 : parsed }));
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Payment Date *
                </label>
                <div className="relative">
                  <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Payment Method *
                </label>
                <select
                  value={form.method}
                  onChange={(e) => handleInputChange('method', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="card">Credit Card</option>
                  <option value="zelle">Zelle</option>
                </select>
              </div>

              {form.method === 'check' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Check Number
                  </label>
                  <input
                    type="text"
                    value={form.reference}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                    placeholder="Check number"
                  />
                </div>
              )}

              {form.method === 'zelle' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Zelle Reference
                  </label>
                  <input
                    type="text"
                    value={form.reference}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                    placeholder="Email, phone, or transaction ID"
                  />
                </div>
              )}

              {form.method === 'card' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="useStripe"
                    checked={form.useStripe}
                    onChange={(e) => handleInputChange('useStripe', e.target.checked)}
                    className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="useStripe" className="text-sm text-neutral-700 dark:text-neutral-300">
                    Process via Stripe
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Memo
                </label>
                <textarea
                  value={form.memo}
                  onChange={(e) => handleInputChange('memo', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-neutral-800 dark:text-white"
                  placeholder="Optional memo"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              {customerCredit > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <p className="text-blue-800 dark:text-blue-200">
                    💳 Available Credit: ${customerCredit.toFixed(2)}
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-4">
                  Select Invoices to Pay
                </h3>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : invoices.length === 0 ? (
                  <p className="text-neutral-600 dark:text-neutral-400 text-center py-8">
                    No unpaid invoices found for this customer.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Waive tax toggle */}
                    <div className="flex items-center space-x-2 p-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-neutral-800">
                      <input
                        type="checkbox"
                        id="waiveTax"
                        checked={waiveTax}
                        onChange={(e) => setWaiveTax(e.target.checked)}
                        className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="waiveTax" className="text-sm text-neutral-800 dark:text-neutral-200">
                        Waive tax for selected invoices (excludes Account Balance/SBAL)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 p-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-neutral-50 dark:bg-neutral-800">
                      <input
                        type="checkbox"
                        id="autoApplyCredit"
                        checked={form.autoApplyCredit}
                        onChange={(e) => handleInputChange('autoApplyCredit', e.target.checked)}
                        className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="autoApplyCredit" className="text-sm text-neutral-800 dark:text-neutral-200">
                        Apply remaining credit to open invoices now
                      </label>
                    </div>
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center space-x-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-md">
                        <input
                          type="checkbox"
                          checked={form.selectedInvoices.includes(invoice.id)}
                          onChange={() => handleInvoiceToggle(invoice.id)}
                          className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-neutral-900 dark:text-neutral-50">#{invoice.number}</div>
                              <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                Due: {new Date(invoice.due_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-neutral-600 dark:text-neutral-400">${invoice.balance_due.toFixed(2)}</div>
                              {/* Pre-tax balance display under balance */}
                              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                                {(() => {
                                  const sub = Number(invoice.sub_total || 0);
                                  const tax = Number(invoice.tax_total || 0);
                                  const total = sub + tax;
                                  const preTax = total > 0 ? Math.round((invoice.balance_due * (sub / total)) * 100) / 100 : invoice.balance_due;
                                  return (
                                    <span>Pre-tax Balance: ${preTax.toFixed(2)}</span>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {form.selectedInvoices.length > 0 && (
                <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-md">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Selected invoices total: <span className="font-medium">${calculateTotalSelected().toFixed(2)}</span>
                  </p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Payment amount: <span className="font-medium">${form.amount.toFixed(2)}</span>
                  </p>
                  {form.amount > calculateTotalSelected() && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                      Remaining ${(form.amount - calculateTotalSelected()).toFixed(2)} will be {form.autoApplyCredit ? 'applied to open invoices when possible.' : 'stored as credit.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

                      {currentStep === 3 && allocationPreview && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-4">
                    Payment Allocation Preview
                  </h3>
                  
                  {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-red-800 dark:text-red-200 mb-2">{error}</p>
                      <button
                        onClick={() => setError('')}
                        className="text-sm text-red-600 dark:text-red-400 hover:underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                
                {allocationPreview.selectedInvoices.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-50 mb-2">
                      Selected Invoices:
                    </h4>
                    <div className="space-y-2">
                      {allocationPreview.selectedInvoices.map((invoice: Invoice) => (
                        <div key={invoice.id} className="flex justify-between items-center p-2 bg-neutral-50 dark:bg-neutral-800 rounded">
                          <span>#{invoice.number}</span>
                          <span className="font-medium">${invoice.balance_due.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {allocationPreview.appliedToOtherInvoices.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-50 mb-2">
                      Additional Allocation:
                    </h4>
                    <div className="space-y-2">
                      {allocationPreview.appliedToOtherInvoices.map((invoice: Invoice & { previewAppliedAmount?: number }) => (
                        <div key={invoice.id} className="flex justify-between items-center p-2 bg-neutral-50 dark:bg-neutral-800 rounded">
                          <span>#{invoice.number}</span>
                          <span className="font-medium">${Number(invoice.previewAppliedAmount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {allocationPreview.creditAmount > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                    <p className="text-blue-800 dark:text-blue-200">
                      Credit to be created: <span className="font-medium">${allocationPreview.creditAmount.toFixed(2)}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft className="mr-2" />
            Back
          </button>

          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              Cancel
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                disabled={!canProceedToStep2() || loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <FiChevronRight className="ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FiCheck className="mr-2" />
                    Create Payment
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentWizard; 
